import { motion } from "framer-motion";
import { TreeStructure, MapTrifold, Path } from "@phosphor-icons/react";
import { Mascot } from "../styleguide/Mascot";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export function ArchitectureHero() {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-blue/15 blur-3xl" />
        <div className="absolute right-0 top-10 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-56 w-56 rounded-full bg-teal/15 blur-3xl" />
      </div>

      <div className="relative mx-auto grid w-full max-w-content items-center gap-10 px-5 py-14 sm:px-8 md:py-20 lg:grid-cols-[1.2fr_0.8fr] lg:px-12">
        <div>
          <motion.span
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-1.5 text-sm font-bold uppercase tracking-wider text-accent-foreground"
          >
            <TreeStructure size={16} weight="fill" /> Experience Architecture
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-balance md:text-5xl"
          >
            The learning ecosystem children <span className="text-primary-strong">grow up inside.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-5 max-w-xl text-lg font-medium leading-relaxed text-muted-foreground"
          >
            Not screens — a structure for how children explore, learn, get feedback and grow over the years. Six calm areas, six learning modes, multi-sensory delivery and a memory that celebrates every step from Year 1 to Year 6.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            className="mt-7 flex flex-wrap gap-3"
          >
            <button onClick={() => scrollTo("map")} data-testid="arch-hero-map" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-primary px-7 font-display font-bold text-primary-foreground shadow-glow transition-all duration-200 ease-bounce hover:-translate-y-0.5 active:scale-95">
              <MapTrifold size={20} weight="fill" /> See the navigation map
            </button>
            <button onClick={() => scrollTo("journeys")} data-testid="arch-hero-journeys" className="inline-flex min-h-[52px] items-center gap-2 rounded-full border-2 border-border px-7 font-display font-bold text-foreground transition-all hover:border-primary hover:text-primary-strong">
              <Path size={20} weight="bold" /> Explore journeys
            </button>
          </motion.div>
          <div className="mt-9 grid max-w-md grid-cols-3 gap-3">
            {[["6", "Areas"], ["6", "Modes"], ["Y1–6", "Grows up"]].map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-border bg-card p-3 text-center shadow-soft">
                <p className="font-display text-xl font-black text-primary-strong">{n}</p>
                <p className="text-xs font-bold text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1], delay: 0.1 }}
          className="relative mx-auto flex w-full max-w-xs items-center justify-center"
        >
          <div className="rounded-[2.5rem] border border-border bg-card p-10 shadow-soft-lg">
            <Mascot size={170} mood="thinking" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ArchitectureHero;
